import { SET_CACHE_AUTH, SET_CACHES, SET_SERVERS } from "@/store/mutation-types";

// CacheService ending must be `ANALYTICS`, this module is part of the store and will
// be imported by both Analytics and CacheSight (though it's not used by Analytics)
import CacheService from "@/services/CacheService";

import { RequestMethod } from "@/types/request-method";
import { Login, CachesState, CacheSingle, Server } from "@/types/cachesight";
import { RootState } from "@/types/root-state";
import * as Msg from "@/config/notification";
import { setNotificationSuccess, setNotificationError } from "@/utils/notification";
import { Module, MutationTree, ActionTree, GetterTree } from "vuex";
import Vue from "vue";

const namespaced = true;

const state: CachesState = {
  caches: null,
};

const mutations: MutationTree<CachesState> = {
  [SET_CACHES]: (state, caches: CacheSingle[]) => {
    state.caches = caches;
  },
  [SET_SERVERS]: (state, payload: { cacheIndex: number; servers: Server[] }) => {
    if (state.caches) {
      Vue.set(state.caches[payload.cacheIndex], "servers", payload.servers);
    }
  },
};

const actions: ActionTree<CachesState, RootState> = {
  // CacheSight login
  login({ commit, dispatch }, login: Login) {
    return CacheService.request(`login`, RequestMethod.POST, {}, login)
      .then(response => {
        const login: { token: string } = response.data;
        commit(SET_CACHE_AUTH, login.token, { root: true });
        dispatch("fetch");
        return true;
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.LOGIN_ERROR} (${error})`);
        return false;
      });
  },
  password({ commit, dispatch }, passwords: { old: string; new: string }) {
    return CacheService.request(`password`, RequestMethod.POST, {}, passwords)
      .then(response => {
        // Save new token
        const password: { token: string } = response.data;
        commit(SET_CACHE_AUTH, password.token, { root: true });
        setNotificationSuccess(dispatch, Msg.PASSWORD_SUCCESS);
        return true;
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.PASSWORD_ERROR} (${error})`);
        return false;
      });
  },
  fetch({ commit }) {
    return CacheService.request(`caches`, RequestMethod.GET).then(response => {
      const caches: CacheSingle[] = response.data;
      commit(SET_CACHES, caches);
    });
  },
  add({ dispatch }, cache: CacheSingle) {
    if (cache.servers) {
      for (const server of cache.servers) {
        // handles the case where empty server field(s) have been submitted
        if (!server.url && !server.port) continue;
      }
    }

    return CacheService.request(`caches`, RequestMethod.POST, {}, cache)
      .then(() => {
        // fetch so store is up to date
        dispatch("fetch");
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.CACHE_ADD_ERROR} (${error})`);
      });
  },
  update({ dispatch, rootState }, cache: CacheSingle) {
    // servers can only be added or deleted, not updated
    // save before deleting from cache object
    const servers = cache.servers;

    // cache update data should not have servers
    delete cache.servers;

    const cacheID = rootState.activeCacheID;

    return CacheService.request(`caches/${cacheID}`, RequestMethod.PUT, {}, cache)
      .then(() => {
        // add new servers
        if (servers) {
          for (const server of servers) {
            const data: Server = {
              cache_id: cache.id,
              url: server.url,
              port: server.port,
            };

            CacheService.request(`caches/${cacheID}/servers`, RequestMethod.POST, {}, data)
              .then(() => {
                // fetch so servers are up to date in store
                dispatch("fetchServers");
              })
              .catch(error => {
                setNotificationError(dispatch, `${Msg.SERVER_UPDATE_ERROR} (${error})`);
              });
          }
        }

        // fetch so cache is up to date in store
        dispatch("fetch");
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.CACHE_UPDATE_ERROR} (${error})`);
      });
  },
  remove({ dispatch, rootState }) {
    const cacheID = rootState.activeCacheID;
    return CacheService.request(`caches/${cacheID}`, RequestMethod.DELETE)
      .then(() => {
        // fetch so store is up to date
        dispatch("fetch");
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.CACHE_REMOVE_ERROR} (${error})`);
      });
  },
  fetchServers({ commit, getters, rootState }) {
    const cacheID = rootState.activeCacheID;
    return CacheService.request(`caches/${cacheID}/servers`, RequestMethod.GET).then(response => {
      const servers: Server[] = response.data;
      const payload = {
        cacheIndex: getters.activeCacheIndex,
        servers: servers,
      };
      commit(SET_SERVERS, payload);
    });
  },
  removeServer({ dispatch, rootState }, serverId: number) {
    const cacheID = rootState.activeCacheID;
    return CacheService.request(`caches/${cacheID}/servers/${serverId}`, RequestMethod.DELETE)
      .then(() => {
        // fetch so store is up to date
        dispatch("fetchServers");
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.SERVER_REMOVE_ERROR} (${error})`);
      });
  },
};

const getters: GetterTree<CachesState, RootState> = {
  activeCache: (state, getters, rootState) => {
    return state.caches?.find(cache => cache.id === rootState.activeCacheID);
  },
  activeCacheIndex: (state, getters, rootState) => {
    return state.caches?.findIndex(cache => cache.id === rootState.activeCacheID);
  },
};

export const caches: Module<CachesState, RootState> = {
  namespaced,
  state,
  mutations,
  actions,
  getters,
};
